<template>
  <div class="contents">
    <div class="d-flex section-title-with-back">
      <img src="@/assets/images/Icon metro-lock_bk.png" alt="" width="27.3" height="27.3" />
      <p class="title-bk">凍結ID管理</p>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <router-link :to="{ name: 'home' }">戻る</router-link>
    </div>
    <div class="container">
      <!--tab-->
      <ul class="nav nav-tabs">
        <li class="nav-item" id="tabmenu">
          <a class="nav-link active tabitem">凍結中</a>
        </li>
      </ul>

      <div class="d-flex form-inline">
        <button type="button" class="btn btn-gray" @click="exportToExcel">
          エクスポート
        </button>
        <button type="button" class="btn btn-gray" @click="exportToPdf">プリント</button>
        <div class="form-group ml-auto">
          <label for="text1">検索:</label>
          <input type="text" id="searchform_small" class="form-control" v-model="filter" />
        </div>
      </div>

      <div class="form-sort align-items-center">
        <div class="item-count col-2 my-1">
          <label class="mr-sm-2 sr-only" for="inlineFormCustomSelectSmall">
            Preference
          </label>
          <select class="custom-select mr-sm-2" id="inlineFormCustomSelectSmall" v-model="perPage">
            <option :value="totalFrozens" selected>全</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="150">150</option>
          </select>
          件表示
        </div>
      </div>

      <div class="tab-content">
        <div id="table-1" class="tab-pane active">
          <div>
            <b-table
              ref="frozen-table"
              :min-width="tableWidth"
              class="sticky-table table-auto-height"
              responsive
              sticky-header
              foot-clone
              no-footer-sorting
              hover
              :fields="fields"
              :filter="filter"
              :filterIncludedFields="searchableFields"
              :per-page="perPage"
              :current-page="currentPage"
              :items="resourcesFiltered"
              :refreshed="tableRefreshed()"
            >
              <template v-slot:head()="scope">
                <div class="text-nowrap">
                  <b>{{ scope.label }}</b>
                </div>
              </template>

              <template v-slot:cell(correspondence)="row">
                <a class="hyper-text" @click="aimToRestoreFrozenObject(row.item.id)" v-if="row.item.isFrozenRoot">
                  復元
                </a>
              </template>

              <template v-slot:foot()="data">
                <div class="footFilters" v-show="data.field.footFilterable">
                  <v-select
                    tag="div"
                    v-model="footFilters[data.column]"
                    placeholder="フィルタ"
                    class="select-control"
                    :options="tableFootOptions[data.column]"
                    :appendToBody="true"
                  />
                </div>
              </template>
            </b-table>
          </div>
          <div class="pagination-footer">
            <label> {{ totalFrozens }} 件中 {{ baseIndex }} から {{ capIndex }} まで表示 </label>
            <div class="float-right">
              <button :disabled="!hasPreviousPage" type="button" class="btn btn-invisible" @click="goToPreviousPage">
                前
              </button>
              <label class="pagination-border text-center">{{ currentPage }}</label>
              <button :disabled="!hasNextPage" type="button" class="btn btn-invisible" @click="goToNextPage">
                次
              </button>
            </div>
          </div>
        </div>
      </div>
      <PopUpHolder
        ref="popup_holder"
        no-close-on-esc
        no-close-on-backdrop
        show-red-button
        green-button-title="戻る"
        red-button-title="実行"
        @greenClick="popupCancelled"
        @redClick="popupConfirmed"
      >
        <template v-slot:header>凍結IDの復元</template>
        <template v-slot:body>
          <RestoreReviewTable />
        </template>
      </PopUpHolder>
      <PopUpHolder
        ref="restore_frozen_fail"
        no-close-on-esc
        no-close-on-backdrop
        green-button-title="閉じる"
        @greenClick="restoreFrozenFailConfirmed"
      >
        <template v-slot:header>エラー</template>
        <template v-slot:body>
          <p>以下いずれかの理由で凍結解除できません</p>
          <p>
            ・法人の利用教材に含まれない教場が存在するため<br />
            ・教場の利用教材に含まれない映像視聴IDが存在するため
          </p>
        </template>
      </PopUpHolder>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import store from '@/store/index.js';
import { sumObjectArrayByKey } from '@/utils/math_helpers.js';
import { updateTableHeight, resourcesFiltered, tableFootOptions } from '@/helpers/table';
import { updateStickyColumnsAndMinWidth } from '@/helpers/sticky_table';
import {
  basicDatetime,
  basicFrozenType,
  basicFrozenRemainingDays,
  basicFieldPresent,
  basicSubFieldPresent,
} from '@/presenters/fields';
import PopUpHolder from '@/components/popups/PopUpHolder.vue';
import RestoreReviewTable from '@/views/frozens/forms/RestoreReviewTable.vue';
import XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { buildJapaneseFont } from '@/helpers/PdfCustomFont.js';

export default {
  components: {
    PopUpHolder,
    RestoreReviewTable,
  },
  data() {
    var fields = [
      {
        key: 'frozenDatetime',
        enable: true,
        label: '凍結日',
        sortable: true,
        searchable: false,
        thAttr: { width: 200 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.frozenDatetimeDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.frozenDatetimeDisplay(value);
        },
      },
      {
        key: 'type',
        enable: true,
        label: 'ID種別',
        sortable: true,
        searchable: false,
        thAttr: { width: 150 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.frozenTypeDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.frozenTypeDisplay(value);
        },
      },
      {
        key: 'frozenName',
        enable: true,
        label: '名前',
        sortable: true,
        searchable: false,
        thAttr: { width: 200 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return basicFieldPresent(item.frozen, 'name');
        },
      },
      {
        key: 'frozenId',
        enable: true,
        label: 'ID',
        sortable: true,
        searchable: false,
        thAttr: { width: 200 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return basicFieldPresent(item.frozen, 'uid');
        },
      },
      {
        key: 'frozenBy',
        enable: true,
        label: '凍結実行者',
        sortable: true,
        searchable: false,
        thAttr: { width: 200 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.frozenByDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.frozenByDisplay(value);
        },
      },
      {
        key: 'frozenRoot',
        enable: true,
        label: '凍結元ID',
        sortable: true,
        searchable: false,
        thAttr: { width: 200 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (value) => {
          return this.frozenRootDisplay(value);
        },
        footFilterable: true,
        footOptions: (value) => {
          return this.frozenRootDisplay(value);
        },
      },
      {
        key: 'remainingDays',
        enable: true,
        label: '削除まで',
        sortable: true,
        searchable: false,
        thAttr: { width: 200 },
        sortByFormatted: true,
        filterByFormatted: true,
        formatter: (_v1, _v2, item) => {
          return this.remainingDaysDisplay(item.frozenDatetime);
        },
      },
      {
        key: 'correspondence',
        enable: true,
        label: '',
        sortable: false,
        searchable: false,
        class: 'sticky-column right-sticky-column-1',
        thAttr: { width: 100 },
      },
    ];
    return {
      fields: fields,
      currentPage: 1,
      perPage: 50,
      selectAll: false,
      filter: null,
      currentFrozenObject: null,
      footFilters: {},
    };
  },
  mounted() {},
  beforeRouteEnter(routeTo, routeFrom, next) {
    store.dispatch('frozen/fetchFrozens').then(() => {
      next();
    });
  },
  computed: {
    resources() {
      return this.frozen.frozens;
    },
    resourcesFiltered() {
      return resourcesFiltered(this.resources, this.fields, this.footFilters);
    },
    tableFootOptions() {
      return tableFootOptions(this.resources, this.fields);
    },
    baseIndex() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    capIndex() {
      return Math.min(this.baseIndex + this.perPage - 1, this.totalFrozens);
    },
    hasPreviousPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.totalFrozens > this.currentPage * this.perPage;
    },
    tableWidth() {
      return sumObjectArrayByKey(this.fields, 'thAttr', 'width') + 'px';
    },
    searchableFields() {
      var fields = this.fields.filter((field) => field.enable == true && field.searchable == true);
      return fields.map((field) => field.key);
    },
    ...mapState(['frozen']),
    ...mapGetters('frozen', ['getFrozenById', 'totalFrozens', 'getExcelFormattedFrozens', 'getExcelHeaderFrozens']),
  },
  methods: {
    tableRefreshed() {
      this.$logger.debug('Table refreshed');
      updateTableHeight();
      updateStickyColumnsAndMinWidth({ leftColumns: 0, rightColumns: 1 });
    },
    goToPreviousPage() {
      this.selectAll = false;
      this.currentPage--;
    },
    goToNextPage() {
      this.selectAll = false;
      this.currentPage++;
    },
    frozenTypeDisplay(type) {
      return basicFrozenType(type);
    },
    aimToRestoreFrozenObject(id) {
      this.$logger.debug('Restore Frozen Object with Id: ' + id);
      this.currentFrozenObject = this.getFrozenById(id);
      this.fetchRelatedFrozens(id).then(() => {
        this.$refs['popup_holder'].show();
      });
    },
    frozenDatetimeDisplay(field) {
      return basicDatetime(field);
    },
    remainingDaysDisplay(date) {
      return basicFrozenRemainingDays(date);
    },
    frozenByDisplay(frozenBy) {
      if (frozenBy.username) {
        return frozenBy.username;
      }
      return basicFieldPresent(frozenBy, 'id');
    },
    frozenRootDisplay(frozenRoot) {
      if (frozenRoot.frozen) {
        return basicSubFieldPresent(frozenRoot, 'frozen', 'uid');
      }
      return basicFieldPresent(frozenRoot, 'uid');
    },
    popupCancelled() {
      this.$logger.debug('Not going to restore Objects');
    },
    restoreFrozenFailConfirmed() {
      this.$logger.debug('Restore Frozen Fail Confirmed Popup done');
    },
    popupConfirmed() {
      this.$logger.debug('Starting to restore Objects');
      this.restoreFrozen(this.currentFrozenObject.id)
        .then(() => {
          this.fetchFrozens();
        })
        .catch(() => {
          this.$refs['restore_frozen_fail'].show();
        });
    },
    exportToExcel() {
      this.$logger.debug('Export Frozens to Excel');
      var frozenWS = XLSX.utils.json_to_sheet(this.getExcelFormattedFrozens);
      var wb = XLSX.utils.book_new(); // make Workbook of Excel
      XLSX.utils.book_append_sheet(wb, frozenWS, 'Frozens'); // add Worksheet to Workbook
      XLSX.writeFile(wb, 'frozens.xlsx');
    },
    createHeaders(keys) {
      return keys.map((key) => ({
        name: key,
        prompt: key,
        width: 55,
        align: 'center',
        padding: 0,
      }));
    },
    exportToPdf() {
      this.$logger.debug('Print Frozens to PDF');
      let doc = new jsPDF({ orientation: 'l', format: 'a4' });
      buildJapaneseFont(doc);
      doc.table(4, 4, this.getExcelFormattedFrozens, this.createHeaders(this.getExcelHeaderFrozens));
      doc.autoPrint();
      if (this.getExcelFormattedFrozens.length > 150) {
        this.$logger.debug('Data too big, can only save');
        doc.save('frozens.pdf', { returnPromise: true }).then(() => {
          this.$logger.debug('PDF saved successfully');
        });
      } else {
        this.$logger.debug('Data OK, Print from new window');
        doc.output('dataurlnewwindow', { filename: 'frozens.pdf' });
      }
    },
    ...mapActions('frozen', ['restoreFrozen', 'fetchFrozens', 'fetchRelatedFrozens']),
  },
};
</script>

<style lang="scss" scoped>
.btn-sm {
  margin-left: 0px;
}
.btn-right {
  margin-left: 12px;
}
.btn-gray {
  margin-bottom: 5px;
}
.hyper-text {
  color: #227afa;
  cursor: pointer;
}
.item-count {
  padding: 0;
  font-size: 10px;
}
.pagination-footer {
  margin-top: 20px;
}

/deep/ tr {
  &:hover {
    background-color: B5D3FF;
    td {
      z-index: 1;
      background-color: transparent;
    }
  }
}
</style>
